<template>
	<transition name="preview-fade">
		<div
			class="preview-file"
			v-if="show"
			role="dialog"
			aria-modal="true"
			@click.self="close"
			@keydown.esc="close"
			tabindex="-1"
		>
			<div class="mask" @click="close"></div>
			<div class="preview-container" :class="{ 'is-image-preview': isImagePreviewMode }" @click.stop>
				<div class="preview-frame" :class="{ 'is-image-preview': isImagePreviewMode }">
					<div class="preview-toolbar">
						<div class="preview-meta">
							<div class="preview-title" :title="previewTitle">{{ previewTitle }}</div>
							<div v-if="previewSubtitle" class="preview-subtitle" :title="previewSubtitle">{{ previewSubtitle }}</div>
						</div>
						<div class="toolbar-actions">
							<button
								type="button"
								class="toolbar-btn"
								:disabled="!source || isDownloading"
								:title="isDownloading ? '下载中' : '下载'"
								@click="downloadCurrent"
							>
								<i :class="isDownloading ? 'el-icon-loading' : 'el-icon-download'"></i>
							</button>
							<button type="button" class="toolbar-btn" title="关闭" @click="close">
								<i class="el-icon-close"></i>
							</button>
						</div>
					</div>
					<div v-if="isImagePreviewMode" class="preview-stage preview-stage--image">
						<div class="image-stage-shell" :class="{ 'is-loading': isLoading, 'has-error': hasError }">
							<div v-if="isLoading || hasError" class="viewer-placeholder">
								<div class="viewer-status-card">
									<div class="viewer-status-icon">
										<i :class="placeholderIcon"></i>
									</div>
									<div class="viewer-status-title">{{ placeholderTitle }}</div>
									<div class="viewer-status-text">{{ placeholderText }}</div>
									<button
										v-if="hasError"
										type="button"
										class="viewer-status-action"
										:disabled="isDownloading"
										@click="retryPreview"
									>
										{{ placeholderActionText }}
									</button>
								</div>
							</div>
							<img
								v-if="shouldRenderImage"
								:src="previewUrl"
								:alt="currentFileName || 'image-preview'"
								:class="{ ready: !isLoading && !hasError }"
								@load="onImageLoad"
								@error="onImageError"
							/>
						</div>
					</div>
					<div v-else class="preview-stage preview-stage--document">
						<div class="document-shell" :class="{ 'is-loading': isLoading, 'has-error': hasError, 'is-unsupported': showUnsupported }">
							<div v-if="isLoading || hasError || showUnsupported" class="viewer-placeholder">
								<div class="viewer-status-card">
									<div class="viewer-status-icon">
										<i :class="placeholderIcon"></i>
									</div>
									<div class="viewer-status-title">{{ placeholderTitle }}</div>
									<div class="viewer-status-text">{{ placeholderText }}</div>
									<button
										v-if="hasError || showUnsupported"
										type="button"
										class="viewer-status-action"
										:disabled="isDownloading"
										@click="hasError ? retryPreview() : downloadCurrent()"
									>
										{{ placeholderActionText }}
									</button>
								</div>
							</div>
							<iframe v-if="shouldRenderIframe" :src="previewUrl" frameborder="0" @load="onFrameLoad"></iframe>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: "preview-file",
	data() {
		return {
			show: false,
			source: '',
			strict: false,
			previewUrl: '',
			downloadUrl: '',
			currentFileName: '',
			currentContentType: '',
			bodyOverflow: '',
			isLoading: false,
			hasError: false,
			errorText: '',
			isDownloading: false,
			canInlinePreview: true,
			requestId: 0,
			currentPayload: null
		}
	},
	mounted() {
		document.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleKeydown);
		this.restoreBodyOverflow();
	},
	methods: {
		async open(payload) {
			const filePayload = typeof payload === 'string' ? { url: payload } : (payload || {});
			const currentRequestId = ++this.requestId;
			this.currentPayload = { ...filePayload };
			this.bodyOverflow = document.body.style.overflow;
			this.show = true;
			this.source = filePayload.url || '';
			this.strict = Boolean(filePayload.strict);
			this.previewUrl = '';
			this.downloadUrl = '';
			this.currentFileName = filePayload.fileName || '';
			this.currentContentType = filePayload.contentType || '';
			this.isLoading = true;
			this.hasError = false;
			this.errorText = '';
			this.isDownloading = false;
			this.canInlinePreview = this.isInlinePreviewSupported(this.currentContentType, this.currentFileName);
			document.body.style.overflow = 'hidden';
			this.$nextTick(() => {
				if (this.$el && this.$el.focus) {
					this.$el.focus();
				}
			});
			if (!this.source) {
				this.handlePreviewError('文件暂不可用');
				return;
			}
			if (this.strict && !this.$assetResolver.hasAssetSource(this.source)) {
				this.handlePreviewError('文件暂不可用');
				return;
			}
			// 对于明确无法在线预览的类型，直接展示下载占位，避免先完整拉取一次大文件。
			if (!this.canInlinePreview && (this.currentFileName || this.currentContentType)) {
				this.isLoading = false;
				return;
			}
			try {
				const resolved = await this.$assetResolver.resolve(this.source, {
					fileName: this.currentFileName,
					contentType: this.currentContentType
				});
				if (currentRequestId !== this.requestId) {
					return;
				}
				this.downloadUrl = resolved.url;
				this.currentFileName = resolved.fileName || this.currentFileName;
				this.currentContentType = resolved.contentType || this.currentContentType;
				this.canInlinePreview = this.isInlinePreviewSupported(this.currentContentType, this.currentFileName);
				this.previewUrl = this.canInlinePreview ? resolved.url : '';
				if (!this.canInlinePreview) {
					this.isLoading = false;
				}
			} catch (error) {
				if (currentRequestId !== this.requestId) {
					return;
				}
				this.handlePreviewError(error && error.message ? error.message : '文件加载失败');
			}
		},
		close() {
			this.requestId += 1;
			this.show = false;
			this.source = '';
			this.strict = false;
			this.previewUrl = '';
			this.downloadUrl = '';
			this.currentFileName = '';
			this.currentContentType = '';
			this.isLoading = false;
			this.hasError = false;
			this.errorText = '';
			this.isDownloading = false;
			this.canInlinePreview = true;
			this.currentPayload = null;
			this.restoreBodyOverflow();
		},
		retryPreview() {
			if (!this.currentPayload) {
				return;
			}
			this.open({ ...this.currentPayload });
		},
		handleKeydown(e) {
			if (e.key === 'Escape' && this.show) {
				this.close();
			}
		},
		onImageLoad() {
			this.isLoading = false;
		},
		onImageError() {
			this.handlePreviewError('文件加载失败');
		},
		onFrameLoad() {
			this.isLoading = false;
		},
		handlePreviewError(message) {
			this.previewUrl = '';
			this.downloadUrl = '';
			this.isLoading = false;
			this.hasError = true;
			this.errorText = message || '文件加载失败';
		},
		isInlinePreviewSupported(contentType, fileName) {
			const lowerType = String(contentType || '').toLowerCase();
			if (lowerType.startsWith('image/') || lowerType.startsWith('text/') || lowerType === 'application/pdf') {
				return true;
			}
			const extension = this.getFileExtension(fileName);
			return ['pdf', 'txt', 'text', 'md', 'json', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'html', 'htm'].includes(extension);
		},
		isImageFile(contentType, fileName) {
			const lowerType = String(contentType || '').toLowerCase();
			if (lowerType.startsWith('image/')) {
				return true;
			}
			return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(this.getFileExtension(fileName));
		},
		getFileExtension(fileName) {
			const normalized = String(fileName || '');
			const index = normalized.lastIndexOf('.');
			if (index < 0) {
				return '';
			}
			return normalized.slice(index + 1).toLowerCase();
		},
		async downloadCurrent() {
			if (!this.source || this.isDownloading) {
				return;
			}
			this.isDownloading = true;
			try {
				await this.$assetResolver.download(this.source, {
					fileName: this.currentFileName
				});
				this.$message.success('文件下载已开始');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '文件下载失败');
			} finally {
				this.isDownloading = false;
			}
		},
		restoreBodyOverflow() {
			document.body.style.overflow = this.bodyOverflow || '';
		}
	},
	computed: {
		previewTitle() {
			return this.currentFileName || '文件预览';
		},
		previewSubtitle() {
			if (this.currentContentType) {
				return this.currentContentType;
			}
			const extension = this.getFileExtension(this.currentFileName);
			return extension ? `.${extension}` : '';
		},
		isImagePreviewMode() {
			return this.isImageFile(this.currentContentType, this.currentFileName);
		},
		shouldRenderImage() {
			return Boolean(this.previewUrl && this.canInlinePreview && !this.hasError && this.isImagePreviewMode);
		},
		shouldRenderIframe() {
			return Boolean(this.previewUrl && this.canInlinePreview && !this.hasError && !this.isImagePreviewMode);
		},
		showUnsupported() {
			return !this.isLoading && !this.hasError && !this.canInlinePreview;
		},
		placeholderTitle() {
			if (this.isLoading) {
				return '正在加载文件预览';
			}
			if (this.hasError) {
				return '文件暂时无法打开';
			}
			return '暂不支持在线预览';
		},
		placeholderText() {
			if (this.isLoading) {
				return '内容准备完成后会自动展示，期间可直接关闭当前弹窗。';
			}
			if (this.hasError) {
				return this.errorText || '文件加载失败，请稍后重试。';
			}
			return '当前文件类型暂不支持在线预览，可直接下载后查看。';
		},
		placeholderIcon() {
			if (this.isLoading) {
				return 'el-icon-loading';
			}
			return this.hasError ? 'el-icon-warning-outline' : 'el-icon-document';
		},
		placeholderActionText() {
			if (this.hasError) {
				return '重新加载';
			}
			return this.isDownloading ? '下载中' : '下载文件';
		}
	}
}
</script>


<style lang="scss">
.preview-file {
	--preview-max-width: min(1360px, calc(100vw - 24px));
	--preview-image-max-width: min(1180px, calc(100vw - 24px));
	--preview-max-height: min(92vh, calc(100vh - 24px));
	--preview-shell-padding: clamp(8px, 1.1vw, 12px);
	--preview-frame-radius: 16px;
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.56);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: var(--im-surface-panel-border);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg);
	--preview-shadow: 0 12px 32px -18px rgba(var(--im-black-rgb), 0.18);
	position: fixed;
	inset: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
	box-sizing: border-box;

	.mask {
		position: absolute;
		inset: 0;
		background: var(--preview-overlay-base);
	}

	.preview-container {
		position: relative;
		width: var(--preview-max-width);
		z-index: 1;
	}

	.preview-container.is-image-preview {
		width: var(--preview-image-max-width);
	}

	.preview-frame {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-height: var(--preview-max-height);
		border-radius: var(--preview-frame-radius);
		overflow: hidden;
		background: var(--preview-frame-bg);
		border: 1px solid var(--preview-frame-border);
		box-shadow: var(--preview-shadow);
	}

	.preview-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		padding: 12px 14px;
		background: var(--preview-frame-bg);
		border-bottom: 1px solid var(--preview-frame-border);
	}

	.preview-meta {
		min-width: 0;
		flex: 1;
	}

	.preview-title {
		font-size: clamp(14px, 1.1vw, 17px);
		font-weight: 600;
		line-height: 1.3;
		color: var(--im-text-color-primary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.preview-subtitle {
		margin-top: 4px;
		font-size: 12px;
		line-height: 1.4;
		color: var(--im-text-color-light);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.toolbar-actions {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;
	}

	.toolbar-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		padding: 0;
		border: 1px solid var(--im-control-border);
		border-radius: 10px;
		background: var(--im-control-bg);
		color: var(--im-icon-color-strong);
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: var(--im-icon-hover-bg);
			border-color: var(--im-control-border-strong);
			color: var(--im-icon-hover-color);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}

		i {
			font-size: 16px;
		}
	}

	.preview-stage {
		min-height: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--preview-stage-bg);
	}

	.preview-stage--document {
		flex: 1;
		min-height: 0;
		padding: var(--preview-shell-padding);
		align-items: stretch;
		justify-content: stretch;
	}

	.document-shell {
		position: relative;
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: stretch;
		min-width: 0;
		min-height: min(520px, calc(100vh - 180px));
		border-radius: calc(var(--preview-frame-radius) - 2px);
		background: var(--preview-shell-bg);
		border: 1px solid var(--preview-frame-border);
		overflow: hidden;
		box-sizing: border-box;

		&.is-loading,
		&.has-error,
		&.is-unsupported {
			min-height: min(420px, calc(100vh - 200px));
		}

		iframe {
			flex: 1;
			width: 100%;
			min-height: 100%;
			border: none;
			background: var(--im-chat-bg);
		}

		.viewer-placeholder {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 20px;
			background: rgba(var(--im-background-rgb), 0.56);
			z-index: 2;
		}

		&.is-loading iframe {
			opacity: 0;
		}
	}

	.preview-stage--image {
		padding: var(--preview-shell-padding);
	}

	.image-stage-shell {
		position: relative;
		width: auto;
		height: auto;
		min-width: min(260px, calc(100vw - 40px));
		min-height: min(180px, calc(100vh - 220px));
		max-width: calc(var(--preview-image-max-width) - (var(--preview-shell-padding) * 2));
		max-height: calc(var(--preview-max-height) - 88px);
		padding: 8px;
		border-radius: calc(var(--preview-frame-radius) - 2px);
		background: var(--preview-shell-bg);
		border: 1px solid var(--preview-frame-border);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-sizing: border-box;

		&.is-loading,
		&.has-error {
			min-width: min(360px, calc(100vw - 40px));
			min-height: min(260px, calc(100vh - 180px));
		}

		img {
			display: block;
			width: auto;
			height: auto;
			max-width: calc(var(--preview-image-max-width) - (var(--preview-shell-padding) * 2) - 24px);
			max-height: calc(var(--preview-max-height) - 112px);
			border-radius: 10px;
			background: var(--im-chat-bg);
			border: 1px solid rgba(var(--im-black-rgb), 0.04);
			opacity: 0;
			transition: opacity 0.18s ease;
			object-fit: contain;
		}

		img.ready {
			opacity: 1;
		}

		.viewer-placeholder {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 20px;
			background: rgba(var(--im-background-rgb), 0.56);
			z-index: 2;
		}
	}

	.viewer-status-card {
		width: min(320px, 100%);
		padding: 18px 16px;
		border-radius: 12px;
		border: 1px solid var(--im-control-border);
		background: var(--im-surface-panel);
		text-align: center;
	}

	.viewer-status-icon {
		width: 36px;
		height: 36px;
		margin: 0 auto 8px;
		border-radius: 999px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);

		i {
			font-size: 16px;
		}
	}

	.viewer-status-title {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: var(--im-text-color-primary);
	}

	.viewer-status-text {
		margin-top: 6px;
		font-size: 12px;
		line-height: 1.6;
		color: var(--im-text-color-light);
	}

	.viewer-status-action {
		min-width: 96px;
		height: 34px;
		margin-top: 14px;
		padding: 0 16px;
		border: 1px solid rgba(var(--im-primary-rgb), 0.18);
		border-radius: 10px;
		background: transparent;
		color: var(--im-color-primary);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: rgba(var(--im-primary-rgb), 0.12);
			border-color: rgba(var(--im-primary-rgb), 0.24);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}
	}

	.document-shell.has-error,
	.image-stage-shell.has-error {
		.viewer-status-icon {
			background: rgba(var(--im-danger-rgb), 0.1);
			color: var(--im-color-danger);
		}
	}
}

[data-theme="dark"] .preview-file {
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.72);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: rgba(var(--im-white-rgb), 0.08);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg-alt);
	--preview-shadow: 0 16px 40px -22px rgba(var(--im-black-rgb), 0.46);
}

.preview-fade-enter-active,
.preview-fade-leave-active {
	transition: opacity 0.24s ease;
}

.preview-fade-enter-active .preview-frame,
.preview-fade-leave-active .preview-frame {
	transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease;
}

.preview-fade-enter,
.preview-fade-leave-to {
	opacity: 0;
}

.preview-fade-enter .preview-frame,
.preview-fade-leave-to .preview-frame {
	opacity: 0;
	transform: translateY(12px) scale(0.985);
}

@media (max-width: 640px) {
	.preview-file {
		--preview-max-width: calc(100vw - 12px);
		--preview-image-max-width: calc(100vw - 12px);
		--preview-max-height: calc(100vh - 12px);
		--preview-shell-padding: 8px;
		--preview-frame-radius: 14px;
		padding: 6px;

		.preview-container {
			width: var(--preview-max-width);
		}

		.preview-container.is-image-preview {
			width: var(--preview-image-max-width);
		}

		.preview-toolbar {
			padding: 10px 10px 8px;
			align-items: flex-start;
			flex-wrap: wrap;
			gap: 8px;
		}

		.preview-meta {
			width: 100%;
		}

		.preview-title {
			font-size: 14px;
		}

		.preview-subtitle {
			font-size: 11px;
		}

		.toolbar-actions {
			gap: 6px;
		}

		.toolbar-btn {
			width: 32px;
			height: 32px;
			border-radius: 9px;

			i {
				font-size: 14px;
			}
		}

		.preview-stage--document {
			padding: 8px;
		}

		.document-shell {
			min-height: min(420px, calc(100vh - 148px));

			&.is-loading,
			&.has-error,
			&.is-unsupported {
				min-height: min(360px, calc(100vh - 160px));
			}
		}

		.image-stage-shell {
			width: 100%;
			min-width: 0;
			min-height: min(200px, calc(100vh - 180px));
			max-height: calc(var(--preview-max-height) - 72px);
			padding: 8px;
			border-radius: 12px;

			&.is-loading,
			&.has-error {
				min-width: 0;
				min-height: min(220px, calc(100vh - 168px));
			}

			img {
				max-width: 100%;
				max-height: calc(var(--preview-max-height) - 98px);
				border-radius: 10px;
			}
		}

		.viewer-status-card {
			padding: 18px 16px;
			border-radius: 14px;
		}

		.viewer-status-title {
			font-size: 14px;
		}

		.viewer-status-text {
			font-size: 11px;
		}
	}
}
</style>
